"use strict";

/*==============================================================================
TRESTLE MEMBERSHIP PAGE

Brand        : Diafemme
File         : trestle-membership.js
Version      : 2.0

Responsibilities
----------------
• Read Shopify-authenticated customer context
• Check Corporate Partner domain eligibility
• Enable Corporate Membership only for approved domains
• Keep Individual Membership available independently
• Recognise existing paid membership tags
• Prevent invalid Corporate purchase attempts
• Guide unapproved organisations to Corporate Partner application
• Handle Free membership CTA
• No secondary authentication
==============================================================================*/


const TrestleMembership = (() => {


/*==============================================================================
RUNTIME
==============================================================================*/

let root = null;

let customerEmail = "";

let customerTags = [];

let corporateDomains = [];

let corporateApplicationUrl = "";


/*==============================================================================
SAFE STRING
==============================================================================*/

function safeString(value){

    if(
        value === null ||
        value === undefined
    ){

        return "";

    }

    return String(value).trim();

}


/*==============================================================================
NORMALIZE TAGS
==============================================================================*/

function normalizeTags(value){

    const text =
        safeString(value);


    if(!text){

        return [];

    }


    return text
        .split(",")
        .map(
            tag =>
                safeString(tag)
                    .toUpperCase()
        )
        .filter(Boolean);

}


/*==============================================================================
NORMALIZE DOMAIN
==============================================================================*/

function normalizeDomain(domain){

    return safeString(domain)
        .toLowerCase()
        .replace(/^@/, "")
        .replace(/\s+/g, "");

}


/*==============================================================================
INITIALIZE
==============================================================================*/

function initialize(){

    root =
        document.getElementById(
            "trMembership"
        );


    if(!root){

        return;

    }


    if(
        root.dataset.membershipInitialized ===
        "true"
    ){

        return;

    }


    root.dataset.membershipInitialized =
        "true";


    loadMembershipContext();

    evaluateMembershipState();

    bindCorporateButton();

    bindFreeButton();


    console.info(
        "TRESTLE Membership Page Ready"
    );

}


/*==============================================================================
LOAD MEMBERSHIP CONTEXT
==============================================================================*/

function loadMembershipContext(){

    customerEmail =
        safeString(
            root.dataset.customerEmail
        ).toLowerCase();


    customerTags =
        normalizeTags(
            root.dataset.customerTags
        );


    corporateDomains =
        safeString(
            root.dataset.corporateDomains
        )
        .split(",")
        .map(
            domain =>
                normalizeDomain(
                    domain
                )
        )
        .filter(Boolean);


    corporateApplicationUrl =
        safeString(
            root.dataset.corporateApplicationUrl
        );

}


/*==============================================================================
GET EMAIL DOMAIN
==============================================================================*/

function getEmailDomain(email){

    const value =
        safeString(email)
            .toLowerCase();


    if(
        !value ||
        !value.includes("@")
    ){

        return "";

    }


    const parts =
        value.split("@");


    if(
        parts.length !== 2
    ){

        return "";

    }


    return normalizeDomain(
        parts[1]
    );

}


/*==============================================================================
CURRENT MEMBERSHIP
==============================================================================*/

function isCorporateActive(){

    return customerTags.includes(
        "TRESTLE_CORPORATE_ACTIVE"
    );

}


function isIndividualActive(){

    return customerTags.includes(
        "TRESTLE_RETAIL_ACTIVE"
    );

}


/*==============================================================================
CORPORATE ELIGIBILITY
==============================================================================*/

function isCorporateEligible(){

    const domain =
        getEmailDomain(
            customerEmail
        );


    if(!domain){

        return false;

    }


    return corporateDomains.includes(
        domain
    );

}


/*==============================================================================
EVALUATE MEMBERSHIP STATE
==============================================================================*/

function evaluateMembershipState(){

    /*
    An existing paid member should not be encouraged
    to purchase another membership.
    */

    if(
        isCorporateActive() &&
        isCorporateEligible()
    ){

        showExistingMembership(
            "CORPORATE"
        );

        return;

    }


    if(
        isIndividualActive()
    ){

        showExistingMembership(
            "INDIVIDUAL"
        );

        return;

    }


    evaluateCorporateEligibility();

}


/*==============================================================================
EXISTING MEMBERSHIP
==============================================================================*/

function showExistingMembership(type){

    const corporateButton =
        document.getElementById(
            "trMembershipCorporate"
        );


    const retailButton =
        document.getElementById(
            "trMembershipRetail"
        );


    const status =
        document.getElementById(
            "trCorporateStatus"
        );


    if(corporateButton){

        disableButton(
            corporateButton
        );

    }


    if(retailButton){

        disableButton(
            retailButton
        );

    }


    if(status){

        status.classList.remove(
            "is-ineligible"
        );

        status.classList.add(
            "is-eligible"
        );


        status.textContent =

            type === "CORPORATE"

            ?

            "Your TRESTLE Corporate Membership is active."

            :

            "Your TRESTLE Individual Membership is active.";

    }

}


/*==============================================================================
CORPORATE ELIGIBILITY
==============================================================================*/

function evaluateCorporateEligibility(){

    const status =
        document.getElementById(
            "trCorporateStatus"
        );


    const button =
        document.getElementById(
            "trMembershipCorporate"
        );


    if(
        !status ||
        !button
    ){

        return;

    }


    if(!customerEmail){

        return;

    }


    const emailDomain =
        getEmailDomain(
            customerEmail
        );


    if(!emailDomain){

        setCorporateIneligible(

            status,

            button,

            "We couldn't identify a valid email domain for this account."

        );

        return;

    }


    if(!corporateDomains.length){

        setCorporateIneligible(

            status,

            button,

            "Your organisation is not currently registered as a TRESTLE Corporate Partner."

        );

        return;

    }


    if(
        corporateDomains.includes(
            emailDomain
        )
    ){

        setCorporateEligible(

            status,

            button,

            emailDomain

        );

        return;

    }


    setCorporateIneligible(

        status,

        button,

        "Your organisation is not currently registered as a TRESTLE Corporate Partner."

    );

}


/*==============================================================================
SET CORPORATE ELIGIBLE
==============================================================================*/

function setCorporateEligible(
    status,
    button,
    emailDomain
){

    status.classList.remove(
        "is-ineligible"
    );

    status.classList.add(
        "is-eligible"
    );


    status.textContent =
        "Your work email is eligible for TRESTLE Corporate Membership.";


    enableButton(
        button
    );


    button.dataset.corporateDomain =
        emailDomain;

}


/*==============================================================================
SET CORPORATE INELIGIBLE
==============================================================================*/

function setCorporateIneligible(
    status,
    button,
    message
){

    status.classList.remove(
        "is-eligible"
    );

    status.classList.add(
        "is-ineligible"
    );


    status.innerHTML = "";


    const messageElement =
        document.createElement(
            "span"
        );


    messageElement.textContent =
        safeString(message);


    status.appendChild(
        messageElement
    );


    if(corporateApplicationUrl){

        const separator =
            document.createTextNode(
                " "
            );


        const applicationLink =
            document.createElement(
                "a"
            );


        applicationLink.href =
            corporateApplicationUrl;


        applicationLink.textContent =
            "Register your organisation";


        status.appendChild(
            separator
        );


        status.appendChild(
            applicationLink
        );

    }


    disableButton(
        button
    );

}


/*==============================================================================
ENABLE / DISABLE BUTTON
==============================================================================*/

function enableButton(button){

    if(!button){

        return;

    }


    button.classList.remove(
        "is-disabled"
    );


    button.setAttribute(
        "aria-disabled",
        "false"
    );

}


function disableButton(button){

    if(!button){

        return;

    }


    button.classList.add(
        "is-disabled"
    );


    button.setAttribute(
        "aria-disabled",
        "true"
    );

}


/*==============================================================================
CORPORATE BUTTON PROTECTION

Even if CSS fails or somebody attempts to activate the link,
Corporate purchase is blocked unless eligibility is valid.
==============================================================================*/

function bindCorporateButton(){

    const button =
        document.getElementById(
            "trMembershipCorporate"
        );


    if(!button){

        return;

    }


    button.addEventListener(

        "click",

        event => {

            if(
                button.getAttribute(
                    "aria-disabled"
                ) === "true"
            ){

                event.preventDefault();

                return;

            }


            if(
                !isCorporateEligible()
            ){

                event.preventDefault();

            }

        }

    );

}


/*==============================================================================
FREE MEMBERSHIP CTA
==============================================================================*/

function bindFreeButton(){

    const button =
        document.getElementById(
            "trMembershipFree"
        );


    if(!button){

        return;

    }


    button.addEventListener(

        "click",

        () => {

            window.location.href =
                "/pages/trestle-workspace";

        }

    );

}


/*==============================================================================
PUBLIC API
==============================================================================*/

return {

    initialize,


    getCustomerEmail(){

        return customerEmail;

    },


    getCorporateDomains(){

        return [
            ...corporateDomains
        ];

    },


    isCorporateEligible,


    isCorporateActive,


    isIndividualActive

};

})();


/*==============================================================================
PUBLIC NAMESPACE
==============================================================================*/

window.TrestleMembership =
    TrestleMembership;


/*==============================================================================
BOOT
==============================================================================*/

if(
    document.readyState ===
    "loading"
){

    document.addEventListener(

        "DOMContentLoaded",

        () => {

            TrestleMembership.initialize();

        }

    );

}

else{

    TrestleMembership.initialize();

}


/*==============================================================================
TRESTLE MEMBERSHIP PAGE
Version 2.0
COMPLETE
==============================================================================*/